import { createContext, useCallback, useContext, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { AlertTriangle, HelpCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ConfirmOptions {
  title: string;
  /** Explique a consequência da ação com clareza. */
  description?: ReactNode;
  /** Texto explícito do botão de confirmação, ex.: "Excluir ativo". */
  confirmLabel: string;
  cancelLabel?: string;
  /** Ações destrutivas usam botão vermelho. */
  isDestructive?: boolean;
}

export interface PromptOptions extends ConfirmOptions {
  inputLabel: string;
  placeholder?: string;
  minLength?: number;
  isMultiline?: boolean;
}

type DialogRequest =
  | { kind: 'confirm'; options: ConfirmOptions; resolve: (value: boolean) => void }
  | { kind: 'prompt'; options: PromptOptions; resolve: (value: string | null) => void };

interface DialogApi {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  prompt: (options: PromptOptions) => Promise<string | null>;
}

const DialogContext = createContext<DialogApi | null>(null);

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function DialogView({ request, onClose }: { request: DialogRequest; onClose: (result: boolean | string | null) => void }) {
  const titleId = useId();
  const descriptionId = useId();
  const inputId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const [value, setValue] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);
  const { options } = request;
  const isPrompt = request.kind === 'prompt';

  const cancel = useCallback(() => onClose(isPrompt ? null : false), [onClose, isPrompt]);

  const submit = () => {
    if (request.kind === 'prompt') {
      const trimmed = value.trim();
      const minLength = request.options.minLength ?? 1;
      if (trimmed.length < minLength) {
        setInputError(`Informe ao menos ${minLength} caractere${minLength > 1 ? 's' : ''}.`);
        return;
      }
      onClose(trimmed);
      return;
    }
    onClose(true);
  };

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    const firstField = panel?.querySelector<HTMLElement>(isPrompt ? 'input, textarea' : '[data-autofocus]');
    firstField?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        cancel();
        return;
      }
      if (event.key !== 'Tab' || !panel) return;
      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, [cancel, isPrompt]);

  const Icon = options.isDestructive ? AlertTriangle : HelpCircle;

  return (
    <div className="fixed inset-0 z-[110] flex items-end justify-center p-4 sm:items-center">
      <div className="absolute inset-0 bg-black/50 animate-fade-in" onClick={cancel} aria-hidden="true" />
      <div
        ref={panelRef}
        role={options.isDestructive ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={options.description ? descriptionId : undefined}
        className="relative w-full max-w-md rounded-2xl border border-line bg-elevated p-6 shadow-elevation-4 animate-slide-up"
      >
        <div className="flex items-start gap-4">
          <span
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
              options.isDestructive ? 'bg-danger/10 text-danger' : 'bg-brand-500/10 text-brand-600 dark:text-brand-300'
            )}
          >
            <Icon className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="text-lg font-bold text-ink">
              {options.title}
            </h2>
            {options.description && (
              <div id={descriptionId} className="mt-1.5 text-sm text-muted">
                {options.description}
              </div>
            )}
          </div>
        </div>

        {request.kind === 'prompt' && (
          <div className="mt-5">
            <label htmlFor={inputId} className="label">
              {request.options.inputLabel}
            </label>
            {request.options.isMultiline ? (
              <textarea
                id={inputId}
                rows={3}
                className="input resize-none"
                placeholder={request.options.placeholder}
                value={value}
                aria-invalid={inputError ? true : undefined}
                onChange={(event) => {
                  setValue(event.target.value);
                  setInputError(null);
                }}
              />
            ) : (
              <input
                id={inputId}
                className="input"
                placeholder={request.options.placeholder}
                value={value}
                aria-invalid={inputError ? true : undefined}
                onChange={(event) => {
                  setValue(event.target.value);
                  setInputError(null);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') submit();
                }}
              />
            )}
            {inputError && (
              <p className="mt-1.5 text-sm text-danger" role="alert">
                {inputError}
              </p>
            )}
          </div>
        )}

        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button type="button" className="btn-secondary" onClick={cancel} data-autofocus={options.isDestructive ? true : undefined}>
            {options.cancelLabel ?? 'Cancelar'}
          </button>
          <button
            type="button"
            className={options.isDestructive ? 'btn-danger' : 'btn-primary'}
            onClick={submit}
            data-autofocus={options.isDestructive ? undefined : true}
          >
            {options.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<DialogRequest | null>(null);

  const confirm = useCallback(
    (options: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        setRequest({ kind: 'confirm', options, resolve });
      }),
    []
  );

  const prompt = useCallback(
    (options: PromptOptions) =>
      new Promise<string | null>((resolve) => {
        setRequest({ kind: 'prompt', options, resolve });
      }),
    []
  );

  const close = useCallback(
    (result: boolean | string | null) => {
      if (!request) return;
      if (request.kind === 'confirm') request.resolve(result === true);
      else request.resolve(typeof result === 'string' ? result : null);
      setRequest(null);
    },
    [request]
  );

  return (
    <DialogContext.Provider value={{ confirm, prompt }}>
      {children}
      {request && <DialogView request={request} onClose={close} />}
    </DialogContext.Provider>
  );
}

function useDialogContext(): DialogApi {
  const context = useContext(DialogContext);
  if (!context) throw new Error('useConfirm precisa estar dentro de <ConfirmDialogProvider>.');
  return context;
}

/** Confirmação acessível: `if (await confirm({...})) { ... }` */
export function useConfirm() {
  return useDialogContext().confirm;
}

/** Pede um texto ao usuário (substitui window.prompt). Retorna null se cancelado. */
export function usePromptDialog() {
  return useDialogContext().prompt;
}
